import * as React from "react";
import { NavMenuOption, PageLayout } from "../../../components/PageLayout/PageLayout";
import { Typography, Grid } from "@mui/material";
import { BreadcrumbItem } from "../../../components/Breadcrumbs/Breadcrumbs";
import { Filter } from "./Filter/Filter";
import { ConnectedLinearChart } from "../../../components/charts/ConnectedLinearChart/ConnectedLinearChart";
import { ChartDataPoint } from "../../../components/charts/ChartDataPoint";
import { EscalationByContactDetailsTable } from "./EscalationByContactDetailsTable/EscalationByContactDetailsTable";
import { SelectOption } from "@mui/base";

const MT_DOC_ESCALATION_TO_HUMAN_EVENT_TYPE = "MT_DOC_ESCALATION_TO_HUMAN";
const MT_MEDIA_ESCALATION_TO_HUMAN_EVENT_TYPE = "MT_MEDIA_ESCALATION_TO_HUMAN";

const buildLinearChartQuery = (
  fromISO: string,
  toISO: string,
  openSearchFilters: Record<string, any>[],
  interval: string
): Record<string, any> => {
  const useCalendarInterval = interval === "1w" || interval === "1M";
  return {
    size: 0,
    query: {
      bool: {
        filter: [...openSearchFilters, { range: { "@timestamp": { gte: fromISO, lt: toISO } } }]
      }
    },
    aggs: {
      events_over_time: {
        date_histogram: {
          field: "@timestamp",
          ...(useCalendarInterval ? { calendar_interval: interval } : { fixed_interval: interval }),
          min_doc_count: 0,
          extended_bounds: { min: fromISO, max: toISO },
          time_zone: "America/Los_Angeles"
        }
      }
    }
  };
};

const parseLinearChartResponse = (response: any): ChartDataPoint[] => {
  const buckets = response?.aggregations?.events_over_time?.buckets ?? [];
  return buckets.map((bucket: any) => ({
    timestamp: bucket.key_as_string || new Date(bucket.key).toISOString(),
    count: bucket.doc_count as number
  }));
};

const breadcrumbs: BreadcrumbItem[] = [
  {
    path: "/analytics",
    label: "Analytics"
  },
  {
    path: "/analytics/mtEscalationByClientContact",
    label: "Machine Translation Escalations by Client Contact"
  }
];

const MtEscalationByClientContactAnalyticsPage: React.FC = () => {
  const now = new Date();
  const [from, setFrom] = React.useState<Date>(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000));
  const [to, setTo] = React.useState<Date>(now);
  const [client, setClient] = React.useState<SelectOption<string>>({ label: "", value: "" });
  const docChartRefetchRef = React.useRef<() => void>(() => {});
  const mediaChartRefetchRef = React.useRef<() => void>(() => {});
  const tableRefetchRef = React.useRef<() => void>(() => {});

  const onFilterChange = React.useCallback((newFrom: Date, newTo: Date, newClient: SelectOption<string>) => {
    setFrom(newFrom);
    setTo(newTo);
    setClient(newClient);
  }, []);

  const onRefresh = React.useCallback(() => {
    if (docChartRefetchRef.current) {
      docChartRefetchRef.current();
    }
    if (mediaChartRefetchRef.current) {
      mediaChartRefetchRef.current();
    }
    if (tableRefetchRef.current) {
      tableRefetchRef.current();
    }
  }, []);

  // Build OpenSearch filters that include the client filter
  const openSearchFilters = React.useMemo(() => {
    const filters: Record<string, any>[] = [];
    if (client?.value) {
      filters.push({ term: { clientId: client.value } });
    }
    return filters;
  }, [client?.value]);

  const fromISO = from.toISOString();
  const toISO = to.toISOString();

  const docChartBuildQuery = React.useCallback(
    (interval: string) =>
      buildLinearChartQuery(
        fromISO,
        toISO,
        [...openSearchFilters, { term: { eventType: MT_DOC_ESCALATION_TO_HUMAN_EVENT_TYPE } }],
        interval
      ),
    [fromISO, toISO, openSearchFilters]
  );

  const mediaChartBuildQuery = React.useCallback(
    (interval: string) =>
      buildLinearChartQuery(
        fromISO,
        toISO,
        [...openSearchFilters, { term: { eventType: MT_MEDIA_ESCALATION_TO_HUMAN_EVENT_TYPE } }],
        interval
      ),
    [fromISO, toISO, openSearchFilters]
  );

  return (
    <PageLayout
      selectedNavMenu={NavMenuOption.ANALYTICS}
      title={
        <Typography variant="h6" sx={{ mb: 2 }}>
          Machine Translation Escalations by Client Contact
        </Typography>
      }
      breadcrumbs={breadcrumbs}
    >
      <Filter from={from} to={to} client={client} onChange={onFilterChange} onRefresh={onRefresh} />

      {client?.value && (
        <>
          <Grid container={true} spacing={2}>
            <Grid item={true} xs={12} sm={6}>
              <ConnectedLinearChart
                id="mt-doc-escalations-by-contact"
                title="Document Escalations Over Time"
                legend="Escalations"
                from={from}
                to={to}
                buildQuery={docChartBuildQuery}
                parseResponse={parseLinearChartResponse}
                refetchRef={docChartRefetchRef}
              />
            </Grid>
            <Grid item={true} xs={12} sm={6}>
              <ConnectedLinearChart
                id="mt-media-escalations-by-contact"
                title="Media Escalations Over Time"
                legend="Escalations"
                from={from}
                to={to}
                buildQuery={mediaChartBuildQuery}
                parseResponse={parseLinearChartResponse}
                refetchRef={mediaChartRefetchRef}
              />
            </Grid>
          </Grid>

          <EscalationByContactDetailsTable from={from} to={to} clientId={client.value} refetchRef={tableRefetchRef} />
        </>
      )}
    </PageLayout>
  );
};

export { MtEscalationByClientContactAnalyticsPage };
